import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const { t, language, setLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Check if we're on the homepage (which has dark hero)
  const isHomePage = location.pathname === '/';
  
  // Use light theme (white text/logo) only on homepage when not scrolled
  const useLightTheme = isHomePage && !isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/services', label: t('nav.services') },
    { to: '/boats', label: t('nav.boats') },
    { to: '/gallery', label: t('nav.gallery') },
    { to: '/events', label: t('nav.events') },
    { to: '/partners', label: t('nav.partners') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-xl border-b border-[#E5E5EA] shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 group flex-shrink-0"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_fd00ee9f-ec40-41d0-8fc4-33af1b0ace01/artifacts/445z42yf_LOGO_Brusa_BLACK_2000.png" 
              alt="Brusa Cantiere Nautico"
              className={`h-12 sm:h-16 w-auto object-contain transition-all duration-300 ${
                useLightTheme ? 'brightness-0 invert' : ''
              }`}
              style={{ aspectRatio: 'auto', maxWidth: '180px' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.to)
                    ? 'text-[#FF3B30]'
                    : useLightTheme 
                      ? 'text-white hover:text-[#FF3B30]'
                      : 'text-[#0B1221] hover:text-[#FF3B30]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side: CTA only */}
          <div className="flex items-center gap-4">

            {/* CTA Button */}
            <Link to="/quote" className="hidden md:block">
              <Button
                data-testid="quote-cta"
                className={`rounded-full px-6 py-2 text-sm font-medium transition-all duration-300 ${
                  useLightTheme 
                    ? 'bg-white text-[#0B1221] hover:bg-white/90'
                    : 'bg-[#0B1221] text-white hover:bg-[#1A2639]'
                }`}
              >
                {t('nav.quote')}
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 transition-colors ${useLightTheme ? 'text-white' : 'text-[#0B1221]'}`}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-[#E5E5EA] animate-fade-in"
        >
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-${link.to.replace('/', '') || 'home'}`}
                className={`block py-2 text-base font-medium ${
                  isActive(link.to)
                    ? 'text-[#FF3B30]'
                    : 'text-[#0B1221]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/quote" className="block pt-4">
              <Button
                data-testid="mobile-quote-cta"
                className="w-full bg-[#0B1221] text-white hover:bg-[#1A2639] rounded-full py-3 text-sm font-medium"
              >
                {t('nav.quote')}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};
